import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Scale, ArrowLeft, ChevronLeft, ChevronRight,
  TrendingUp, TrendingDown, Wallet, Settings, X,
  ArrowUpRight, ArrowDownLeft, CheckCircle, Plus, Trash2, AlertTriangle, RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import { AuthContext } from "../App";
import EmpresaSwitcher from "../components/EmpresaSwitcher";
import { useSSE } from "../hooks/useSSE";
import DateInput from "../components/ui/DateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Constantes ───────────────────────────────────────────────

const MONEDAS = ["PYG", "USD", "BRL", "ARS"];

const MESES_LABEL = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];

const FUENTE_COLOR = {
  "Contratos":           "text-blue-400",
  "Facturas emitidas":   "text-cyan-400",
  "Gastos":              "text-orange-400",
  "Costos fijos":        "text-orange-400",  // legacy: por si vienen registros viejos
  "Sueldos":             "text-violet-400",
  "Facturas recibidas":  "text-red-400",
  "Pago IVA":            "text-amber-400",
  "Retención IVA":       "text-amber-400",
};

function getMesActual() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function finDeMes(periodo) {
  const [year, month] = periodo.split("-").map(Number);
  return new Date(year, month, 0).toISOString().slice(0, 10);
}

function getAnioActual() {
  return new Date().getFullYear();
}

function mesLabel(p) {
  const [y, m] = p.split("-");
  return `${MESES_LABEL[parseInt(m, 10) - 1]} ${y}`;
}

function prevMes(p) {
  const [y, m] = p.split("-").map(Number);
  if (m === 1) return `${y - 1}-12`;
  return `${y}-${String(m - 1).padStart(2, "0")}`;
}

function nextMes(p) {
  const [y, m] = p.split("-").map(Number);
  if (m === 12) return `${y + 1}-01`;
  return `${y}-${String(m + 1).padStart(2, "0")}`;
}

function fmtPYG(n) {
  if (!n && n !== 0) return "₲ 0";
  const abs = Math.abs(n);
  const fmt = `₲ ${Number(abs).toLocaleString("es-PY", { maximumFractionDigits: 0 })}`;
  return n < 0 ? `−${fmt}` : fmt;
}

function fmtUSD(n) {
  if (!n && n !== 0) return "$ 0.00";
  const abs = Math.abs(n);
  const fmt = `$ ${Number(abs).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return n < 0 ? `−${fmt}` : fmt;
}

function fmtBRL(n) {
  if (!n && n !== 0) return "R$ 0,00";
  const abs = Math.abs(n);
  const fmt = `R$ ${Number(abs).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return n < 0 ? `−${fmt}` : fmt;
}

function toFloat(v) {
  const n = parseFloat(v);
  return isNaN(n) ? null : n;
}

// Barra proporcional
function PropBar({ ingreso, egreso }) {
  const total = ingreso + egreso;
  if (!total) return null;
  const pct = Math.round((ingreso / total) * 100);
  return (
    <div className="h-2 rounded-full bg-white/10 overflow-hidden flex">
      <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${pct}%` }} />
      <div className="h-full bg-red-500 flex-1" />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
export default function BalancePage() {
  const { token, activeEmpresaPropia, hasPermission } = useContext(AuthContext);
  const navigate = useNavigate();
  const puedeVerGerencial = hasPermission("reportes.balance_gerencial");

  const [vista, setVista] = useState("mes");  // "mes" | "anual" | "iva"
  const [periodo, setPeriodo] = useState(getMesActual());
  const [anio, setAnio] = useState(getAnioActual());

  const [balanceMes, setBalanceMes] = useState(null);
  const [balanceAnual, setBalanceAnual] = useState(null);
  const [loading, setLoading] = useState(false);

  const [ivaData, setIvaData] = useState(null);
  const [loadingIva, setLoadingIva] = useState(false);

  // Cuentas bancarias (para mostrar desglose por banco)
  const [cuentasBancarias, setCuentasBancarias] = useState([]);

  const [vistaIvaMode, setVistaIvaMode] = useState("mensual"); // "mensual" | "anual" | "todo"
  const [ivaAnualData, setIvaAnualData] = useState([]);
  const [ivaResumen, setIvaResumen] = useState(null);
  const [loadingIvaAnual, setLoadingIvaAnual] = useState(false);
  const [showPagoIvaModal, setShowPagoIvaModal] = useState(false);
  const [pagoIvaForm, setPagoIvaForm] = useState({ descripcion: "", monto: "", fecha_pago: new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10), periodo_iva: getMesActual(), cuenta_id: "", notas: "" });
  const [savingPagoIva, setSavingPagoIva] = useState(false);
  const [detalleBalance, setDetalleBalance] = useState(null);
  const [detalleSearch, setDetalleSearch] = useState("");

  // ── Balance Gerencial (Activo/Pasivo/Patrimonio + cuadro de mando) ────────
  const [balanceGerencial, setBalanceGerencial] = useState(null);
  const [loadingGerencial, setLoadingGerencial] = useState(false);
  const [configGerencial, setConfigGerencial] = useState({ capital_social: 0, capital_social_moneda: "PYG", bienes_de_uso: 0, bienes_de_uso_moneda: "PYG" });
  const [showConfigGerencial, setShowConfigGerencial] = useState(false);
  const [configGerencialForm, setConfigGerencialForm] = useState({ capital_social: "", capital_social_moneda: "PYG", bienes_de_uso: "", bienes_de_uso_moneda: "PYG" });
  const [savingConfigGerencial, setSavingConfigGerencial] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  // ── Fetch ──────────────────────────────────────────────────
  const fetchMes = useCallback(async () => {
    setLoading(true);
    try {
      let q = `?periodo=${periodo}`;
      if (activeEmpresaPropia?.slug) q += `&logo_tipo=${activeEmpresaPropia.slug}`;
      const res = await fetch(`${API}/admin/balance${q}`, { headers });
      if (res.ok) setBalanceMes(await res.json());
    } catch { toast.error("Error al cargar balance"); }
    finally { setLoading(false); }
  }, [periodo, activeEmpresaPropia]); // eslint-disable-line

  const fetchAnual = useCallback(async () => {
    setLoading(true);
    try {
      let q = `?anio=${anio}`;
      if (activeEmpresaPropia?.slug) q += `&logo_tipo=${activeEmpresaPropia.slug}`;
      const res = await fetch(`${API}/admin/balance/anual${q}`, { headers });
      if (res.ok) setBalanceAnual(await res.json());
    } catch { toast.error("Error al cargar balance anual"); }
    finally { setLoading(false); }
  }, [anio, activeEmpresaPropia]); // eslint-disable-line

  const fetchGerencial = useCallback(async () => {
    setLoadingGerencial(true);
    try {
      let q = `?periodo=${periodo}`;
      if (activeEmpresaPropia?.slug) q += `&logo_tipo=${activeEmpresaPropia.slug}`;
      const res = await fetch(`${API}/admin/reportes/balance-gerencial${q}`, { headers });
      if (res.ok) setBalanceGerencial(await res.json());
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || "Error al cargar el Balance Gerencial"); }
    } catch { toast.error("Error al cargar el Balance Gerencial"); }
    finally { setLoadingGerencial(false); }
  }, [periodo, activeEmpresaPropia]); // eslint-disable-line

  const fetchConfigGerencial = useCallback(async () => {
    try {
      let q = "";
      if (activeEmpresaPropia?.slug) q = `?logo_tipo=${activeEmpresaPropia.slug}`;
      const res = await fetch(`${API}/admin/balance-gerencial/config${q}`, { headers });
      if (res.ok) {
        const d = await res.json();
        setConfigGerencial(d);
        setConfigGerencialForm({
          capital_social: d.capital_social ? String(d.capital_social) : "",
          capital_social_moneda: d.capital_social_moneda || "PYG",
          bienes_de_uso: d.bienes_de_uso ? String(d.bienes_de_uso) : "",
          bienes_de_uso_moneda: d.bienes_de_uso_moneda || "PYG",
        });
      }
    } catch { /* silent */ }
  }, [activeEmpresaPropia]); // eslint-disable-line

  const handleSaveConfigGerencial = async (e) => {
    e.preventDefault();
    setSavingConfigGerencial(true);
    try {
      const res = await fetch(`${API}/admin/balance-gerencial/config`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          logo_tipo: activeEmpresaPropia?.slug || "todas",
          capital_social: parseFloat(configGerencialForm.capital_social) || 0,
          capital_social_moneda: configGerencialForm.capital_social_moneda,
          bienes_de_uso: parseFloat(configGerencialForm.bienes_de_uso) || 0,
          bienes_de_uso_moneda: configGerencialForm.bienes_de_uso_moneda,
        }),
      });
      if (!res.ok) { const e2 = await res.json().catch(() => ({})); throw new Error(e2.detail || "Error al guardar"); }
      toast.success("Configuración guardada");
      setShowConfigGerencial(false);
      fetchConfigGerencial();
      fetchGerencial();
    } catch (e2) { toast.error(e2.message); }
    finally { setSavingConfigGerencial(false); }
  };

  const fetchCuentasBancarias = useCallback(async () => {
    try {
      const q = new URLSearchParams();
      if (activeEmpresaPropia?.slug) q.set("logo_tipo", activeEmpresaPropia.slug);
      q.set("desde", `${periodo.slice(0, 4)}-01-01`);
      q.set("hasta", finDeMes(periodo));
      const res = await fetch(`${API}/admin/cuentas-bancarias/saldos${q.toString() ? `?${q}` : ""}`, { headers });
      if (res.ok) setCuentasBancarias(await res.json());
    } catch { /* silent */ }
  }, [activeEmpresaPropia, periodo]); // eslint-disable-line

  const fetchIva = useCallback(async () => {
    setLoadingIva(true);
    try {
      let q = `?periodo=${periodo}`;
      if (activeEmpresaPropia?.slug) q += `&logo_tipo=${activeEmpresaPropia.slug}`;
      const res = await fetch(`${API}/admin/balance/iva${q}`, { headers });
      if (res.ok) setIvaData(await res.json());
    } catch { toast.error("Error al cargar IVA"); }
    finally { setLoadingIva(false); }
  }, [periodo, activeEmpresaPropia]); // eslint-disable-line

  const fetchIvaAnual = useCallback(async () => {
    setLoadingIvaAnual(true);
    try {
      const q = new URLSearchParams({ anio: String(anio) });
      if (activeEmpresaPropia?.slug) q.set("logo_tipo", activeEmpresaPropia.slug);
      const res = await fetch(`${API}/admin/balance/iva/resumen?${q}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setIvaResumen(data);
        setIvaAnualData(data.meses || []);
      }
    } catch { toast.error("Error al cargar IVA anual"); }
    finally { setLoadingIvaAnual(false); }
  }, [anio, activeEmpresaPropia]); // eslint-disable-line

  const fetchIvaTodo = useCallback(async () => {
    setLoadingIvaAnual(true);
    try {
      const q = new URLSearchParams({ todo: "true" });
      if (activeEmpresaPropia?.slug) q.set("logo_tipo", activeEmpresaPropia.slug);
      const res = await fetch(`${API}/admin/balance/iva/resumen?${q}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setIvaResumen(data);
        setIvaAnualData(data.meses || []);
      }
    } catch { toast.error("Error al cargar IVA"); }
    finally { setLoadingIvaAnual(false); }
  }, [activeEmpresaPropia]); // eslint-disable-line

  useEffect(() => {
    fetchCuentasBancarias();
  }, [fetchCuentasBancarias]);

  useEffect(() => {
    if (vista === "mes") fetchMes();
    else if (vista === "anual") fetchAnual();
    else if (vista === "gerencial") { if (puedeVerGerencial) { fetchGerencial(); fetchConfigGerencial(); } else setVista("mes"); }
    else if (vista === "iva") {
      if (vistaIvaMode === "mensual") fetchIva();
      else if (vistaIvaMode === "anual") fetchIvaAnual();
      else fetchIvaTodo();
    }
  }, [vista, fetchMes, fetchAnual, fetchGerencial, fetchConfigGerencial, fetchIva, vistaIvaMode, fetchIvaAnual, fetchIvaTodo, puedeVerGerencial]);

  useSSE({
    logoTipo: activeEmpresaPropia?.slug,
    token,
    enabled: !!activeEmpresaPropia?.slug && !!token,
    onEvent: useCallback((event) => {
      if (event.tipo !== "modulo_actualizado") return;
      const modulo = event.data?.modulo;
      const modulosBalance = [
        "facturas",
        "compras",
        "pagos_proveedores",
        "ingresos_varios",
        "financiero",
        "transferencias",
        "bancos",
        "prestamos",
        "sueldos",
      ];
      if (!modulosBalance.includes(modulo)) return;

      fetchCuentasBancarias();
      if (vista === "mes") fetchMes();
      else if (vista === "anual") fetchAnual();
      else if (vista === "gerencial" && puedeVerGerencial) fetchGerencial();
      else if (vista === "iva") {
        if (vistaIvaMode === "mensual") fetchIva();
        else if (vistaIvaMode === "anual") fetchIvaAnual();
        else fetchIvaTodo();
      }
    }, [fetchAnual, fetchCuentasBancarias, fetchGerencial, fetchIva, fetchIvaAnual, fetchIvaTodo, fetchMes, vista, vistaIvaMode, puedeVerGerencial]),
  });

  const openPagoIvaModal = () => {
    const sugerido = ivaData?.saldo_pendiente_acumulado > 0
      ? ivaData.saldo_pendiente_acumulado
      : Math.max(0, ivaData?.iva_neto || 0);
    const cuentaDefault = cuentasBancarias.find(c => c.moneda === "PYG" && c.activa !== false);
    setPagoIvaForm({
      descripcion: `Pago IVA ${mesLabel(periodo)}`,
      monto: sugerido ? String(Math.round(sugerido)) : "",
      fecha_pago: new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10),
      periodo_iva: periodo,
      cuenta_id: cuentaDefault?.id || "",
      notas: "",
    });
    setShowPagoIvaModal(true);
  };

  const handlePagoIva = async (e) => {
    e.preventDefault();
    if (!pagoIvaForm.monto || Number(pagoIvaForm.monto) <= 0) { toast.error("Completá el monto"); return; }
    if (!pagoIvaForm.periodo_iva) { toast.error("Seleccioná el periodo IVA"); return; }
    if (!pagoIvaForm.cuenta_id) { toast.error("Seleccioná la cuenta bancaria"); return; }
    const cuenta = cuentasBancarias.find(c => c.id === pagoIvaForm.cuenta_id);
    setSavingPagoIva(true);
    try {
      const res = await fetch(`${API}/admin/balance/iva/pagos`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          descripcion: pagoIvaForm.descripcion || `Pago IVA ${mesLabel(pagoIvaForm.periodo_iva)}`,
          periodo_iva: pagoIvaForm.periodo_iva,
          fecha_pago: pagoIvaForm.fecha_pago,
          monto: Math.abs(Number(pagoIvaForm.monto)),
          logo_tipo: activeEmpresaPropia?.slug || "arandujar",
          cuenta_id: pagoIvaForm.cuenta_id,
          cuenta_nombre: cuenta?.nombre || null,
          notas: pagoIvaForm.notas || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Error al registrar pago");
      }
      toast.success("Pago IVA registrado");
      setShowPagoIvaModal(false);
      fetchIva();
      if (vistaIvaMode === "anual") fetchIvaAnual();
      if (vistaIvaMode === "todo") fetchIvaTodo();
      fetchMes();
      fetchCuentasBancarias();
    } catch (e) { toast.error(e.message || "Error al registrar pago IVA"); }
    finally { setSavingPagoIva(false); }
  };

  // ─── Helper UI ────────────────────────────────────────────
  const BalanceChip = ({ valor, big = false }) => {
    const pos = valor >= 0;
    return (
      <span className={`font-heading font-bold ${big ? "text-3xl" : "text-lg"} ${pos ? "text-emerald-400" : "text-red-400"}`}>
        {pos ? "+" : ""}{fmtPYG(valor)}
      </span>
    );
  };

  const openDetalleBalance = (tipo, detalle) => {
    setDetalleSearch("");
    setDetalleBalance({ tipo, ...detalle });
  };

  const detalleItems = (detalleBalance?.items || []).filter(item => {
    const q = detalleSearch.trim().toLowerCase();
    if (!q) return true;
    return [
      item.fecha,
      item.contraparte,
      item.descripcion,
      item.documento,
      item.cuenta_nombre,
      item.referencia_id,
    ].some(v => String(v || "").toLowerCase().includes(q));
  });

  const detalleTotal = detalleItems.reduce((sum, item) => sum + Number(item.monto_pyg || 0), 0);

  const handleRefreshManual = () => {
    fetchCuentasBancarias();
    if (vista === "mes") fetchMes();
    else if (vista === "anual") fetchAnual();
    else if (vista === "iva") {
      if (vistaIvaMode === "mensual") fetchIva();
      else if (vistaIvaMode === "anual") fetchIvaAnual();
      else fetchIvaTodo();
    }
  };

  // ═══════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen bg-arandu-dark">

      {/* Header */}
      <header className="bg-arandu-dark-light border-b border-white/5 px-6 py-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/sistema")} className="p-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-all">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center">
                <Scale className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-white text-xl">Balance & Tesorería</h1>
                <p className="text-slate-500 text-xs">Ingresos, egresos y saldo acumulado</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefreshManual}
              disabled={loading || loadingIva || loadingIvaAnual}
              className="p-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-all disabled:opacity-50"
              title="Actualizar"
            >
              <RefreshCw className={`w-4 h-4 ${(loading || loadingIva || loadingIvaAnual) ? "animate-spin" : ""}`} />
            </button>
            <EmpresaSwitcher compact />
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">

        {/* Vista tabs */}
        <div className="flex items-center gap-4">
          <div className="flex gap-1 bg-white/5 rounded-xl p-1 w-fit">
            {[{ key: "mes", label: "Mensual" }, { key: "anual", label: "Anual" }, ...(puedeVerGerencial ? [{ key: "gerencial", label: "📊 Gerencial" }] : []), { key: "iva", label: "🧾 IVA Fiscal" }].map(v => (
              <button key={v.key} onClick={() => setVista(v.key)}
                className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                  vista === v.key
                    ? v.key === "iva" ? "bg-amber-600 text-white shadow" : v.key === "gerencial" ? "bg-violet-600 text-white shadow" : "bg-teal-600 text-white shadow"
                    : "text-slate-400 hover:text-white"
                }`}>
                {v.label}
              </button>
            ))}
          </div>

          {/* Navegación periodo */}
          {(vista === "mes" || vista === "gerencial" || (vista === "iva" && vistaIvaMode === "mensual")) ? (
            <div className="flex items-center gap-2">
              <button onClick={() => setPeriodo(prevMes(periodo))} className="p-1.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-all">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-white font-heading font-semibold min-w-[110px] text-center">{mesLabel(periodo)}</span>
              <button onClick={() => setPeriodo(nextMes(periodo))} className="p-1.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-all">
                <ChevronRight className="w-5 h-5" />
              </button>
              <button onClick={() => setPeriodo(getMesActual())} className="text-xs text-slate-500 hover:text-white px-3 py-1 rounded border border-white/10 hover:border-white/20 transition-all">
                Hoy
              </button>
            </div>
          ) : vista === "iva" && vistaIvaMode === "todo" ? (
            <div className="text-slate-500 text-sm px-2">Todo el tiempo</div>
          ) : (
            <div className="flex items-center gap-2">
              <button onClick={() => setAnio(a => a - 1)} className="p-1.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-all">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-white font-heading font-semibold text-lg min-w-[60px] text-center">{anio}</span>
              <button onClick={() => setAnio(a => a + 1)} className="p-1.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-all">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-slate-500 text-center py-16">Calculando balance...</div>
        ) : (

          <>
          {/* ══ VISTA MENSUAL ══ */}
          {vista === "mes" && balanceMes && (
            <div className="space-y-6">

              {/* Cards principales */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-emerald-400" />
                    <span className="text-emerald-400 text-xs">Ingresos</span>
                  </div>
                  <p className="text-emerald-300 font-heading font-bold text-2xl">{fmtPYG(balanceMes.total_ingresos)}</p>
                </div>
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingDown className="w-4 h-4 text-red-400" />
                    <span className="text-red-400 text-xs">Egresos</span>
                  </div>
                  <p className="text-red-300 font-heading font-bold text-2xl">{fmtPYG(balanceMes.total_egresos)}</p>
                </div>
                <div className={`border rounded-xl p-4 ${balanceMes.balance >= 0 ? "bg-teal-500/10 border-teal-500/20" : "bg-orange-500/10 border-orange-500/20"}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Scale className={`w-4 h-4 ${balanceMes.balance >= 0 ? "text-teal-400" : "text-orange-400"}`} />
                    <span className={`text-xs ${balanceMes.balance >= 0 ? "text-teal-400" : "text-orange-400"}`}>Balance del mes</span>
                  </div>
                  <BalanceChip valor={balanceMes.balance} big />
                </div>
                <div className="bg-violet-500/10 border border-violet-500/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Wallet className="w-4 h-4 text-violet-400" />
                    <span className="text-violet-400 text-xs">Saldo acumulado</span>
                  </div>
                  <p className={`font-heading font-bold text-2xl ${balanceMes.saldo_acumulado >= 0 ? "text-violet-300" : "text-red-400"}`}>
                    {fmtPYG(balanceMes.saldo_acumulado)}
                  </p>
                  {/* Desglose por banco */}
                  {cuentasBancarias.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-white/10 space-y-1">
                      <p className="text-slate-500 text-xs font-medium">Saldo actual por cuenta:</p>
                      {cuentasBancarias.filter(c => c.moneda === "PYG").map(c => (
                        <div key={c.id} className="flex items-center justify-between">
                          <span className="text-slate-400 text-xs truncate max-w-[120px]">{c.nombre}</span>
                          <span className="text-violet-300 text-xs font-heading font-semibold">
                            {fmtPYG(c.saldo_actual ?? c.saldo_inicial ?? 0)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Mini-cards USD (solo si hay movimientos en USD o saldo acumulado USD) */}
              {((balanceMes.total_ingresos_usd || 0) > 0 || (balanceMes.total_egresos_usd || 0) > 0 || (balanceMes.saldo_acumulado_usd || 0) !== 0) && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <TrendingUp className="w-3.5 h-3.5 text-amber-400" />
                      <span className="text-amber-400 text-xs">Ingresos USD</span>
                    </div>
                    <p className="text-amber-300 font-heading font-semibold text-xl">{fmtUSD(balanceMes.total_ingresos_usd || 0)}</p>
                  </div>
                  <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <TrendingDown className="w-3.5 h-3.5 text-orange-400" />
                      <span className="text-orange-400 text-xs">Egresos USD</span>
                    </div>
                    <p className="text-orange-300 font-heading font-semibold text-xl">{fmtUSD(balanceMes.total_egresos_usd || 0)}</p>
                  </div>
                  {(() => {
                    const balUSD = (balanceMes.total_ingresos_usd || 0) - (balanceMes.total_egresos_usd || 0);
                    return (
                      <div className={`border rounded-xl p-3 ${balUSD >= 0 ? "bg-teal-500/10 border-teal-500/20" : "bg-orange-500/10 border-orange-500/20"}`}>
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <Scale className={`w-3.5 h-3.5 ${balUSD >= 0 ? "text-teal-400" : "text-orange-400"}`} />
                          <span className={`text-xs ${balUSD >= 0 ? "text-teal-400" : "text-orange-400"}`}>Balance USD mes</span>
                        </div>
                        <p className={`font-heading font-semibold text-xl ${balUSD >= 0 ? "text-teal-300" : "text-orange-300"}`}>
                          {balUSD >= 0 ? "+" : "−"}{fmtUSD(Math.abs(balUSD))}
                        </p>
                      </div>
                    );
                  })()}
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Wallet className="w-3.5 h-3.5 text-amber-400" />
                      <span className="text-amber-400 text-xs">Saldo acumulado USD</span>
                    </div>
                    <p className={`font-heading font-semibold text-xl ${(balanceMes.saldo_acumulado_usd || 0) >= 0 ? "text-amber-300" : "text-red-400"}`}>
                      {fmtUSD(balanceMes.saldo_acumulado_usd || 0)}
                    </p>
                    {(balanceMes.superavit_inicial_usd || 0) > 0 && (
                      <p className="text-slate-500 text-xs mt-0.5">Saldo inicial: {fmtUSD(balanceMes.superavit_inicial_usd)}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Mini-cards BRL (solo si hay movimientos en BRL o saldo acumulado BRL) */}
              {((balanceMes.total_ingresos_brl || 0) > 0 || (balanceMes.total_egresos_brl || 0) > 0 || (balanceMes.saldo_acumulado_brl || 0) !== 0) && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <TrendingUp className="w-3.5 h-3.5 text-green-400" />
                      <span className="text-green-400 text-xs">Ingresos BRL</span>
                    </div>
                    <p className="text-green-300 font-heading font-semibold text-xl">{fmtBRL(balanceMes.total_ingresos_brl || 0)}</p>
                  </div>
                  <div className="bg-lime-500/10 border border-lime-500/20 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <TrendingDown className="w-3.5 h-3.5 text-lime-400" />
                      <span className="text-lime-400 text-xs">Egresos BRL</span>
                    </div>
                    <p className="text-lime-300 font-heading font-semibold text-xl">{fmtBRL(balanceMes.total_egresos_brl || 0)}</p>
                  </div>
                  {(() => {
                    const balBRL = (balanceMes.total_ingresos_brl || 0) - (balanceMes.total_egresos_brl || 0);
                    return (
                      <div className={`border rounded-xl p-3 ${balBRL >= 0 ? "bg-teal-500/10 border-teal-500/20" : "bg-lime-500/10 border-lime-500/20"}`}>
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <Scale className={`w-3.5 h-3.5 ${balBRL >= 0 ? "text-teal-400" : "text-lime-400"}`} />
                          <span className={`text-xs ${balBRL >= 0 ? "text-teal-400" : "text-lime-400"}`}>Balance BRL mes</span>
                        </div>
                        <p className={`font-heading font-semibold text-xl ${balBRL >= 0 ? "text-teal-300" : "text-lime-300"}`}>
                          {balBRL >= 0 ? "+" : "−"}{fmtBRL(Math.abs(balBRL))}
                        </p>
                      </div>
                    );
                  })()}
                  <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Wallet className="w-3.5 h-3.5 text-green-400" />
                      <span className="text-green-400 text-xs">Saldo acumulado BRL</span>
                    </div>
                    <p className={`font-heading font-semibold text-xl ${(balanceMes.saldo_acumulado_brl || 0) >= 0 ? "text-green-300" : "text-red-400"}`}>
                      {fmtBRL(balanceMes.saldo_acumulado_brl || 0)}
                    </p>
                    {(balanceMes.superavit_inicial_brl || 0) > 0 && (
                      <p className="text-slate-500 text-xs mt-0.5">Saldo inicial: {fmtBRL(balanceMes.superavit_inicial_brl)}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Barra proporcional */}
              <PropBar ingreso={balanceMes.total_ingresos} egreso={balanceMes.total_egresos} />

              {/* Detalle ingresos / egresos */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {/* Ingresos */}
                <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-emerald-500/5">
                    <ArrowUpRight className="w-4 h-4 text-emerald-400" />
                    <span className="text-emerald-300 font-medium text-sm">Ingresos</span>
                    <span className="ml-auto text-emerald-400 font-heading font-bold">{fmtPYG(balanceMes.total_ingresos)}</span>
                  </div>
                  {balanceMes.ingresos_detalle.length === 0 ? (
                    <p className="text-slate-500 text-sm text-center py-6">Sin ingresos registrados</p>
                  ) : (
                    balanceMes.ingresos_detalle.map(d => (
                      <button
                        key={d.fuente}
                        type="button"
                        onClick={() => openDetalleBalance("Ingreso", d)}
                        className="w-full text-left px-4 py-3 border-b border-white/5 flex items-center justify-between hover:bg-emerald-500/10 transition-colors"
                      >
                        <div>
                          <span className={`text-sm font-medium ${FUENTE_COLOR[d.fuente] || "text-slate-300"}`}>{d.fuente}</span>
                          <span className="text-slate-500 text-xs ml-2">({d.cantidad})</span>
                        </div>
                        <span className="text-emerald-300 font-heading font-semibold text-sm">{fmtPYG(d.monto_pyg)}</span>
                      </button>
                    ))
                  )}
                </div>

                {/* Egresos */}
                <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-red-500/5">
                    <ArrowDownLeft className="w-4 h-4 text-red-400" />
                    <span className="text-red-300 font-medium text-sm">Egresos</span>
                    <span className="ml-auto text-red-400 font-heading font-bold">{fmtPYG(balanceMes.total_egresos)}</span>
                  </div>
                  {balanceMes.egresos_detalle.length === 0 ? (
                    <p className="text-slate-500 text-sm text-center py-6">Sin egresos registrados</p>
                  ) : (
                    balanceMes.egresos_detalle.map(d => (
                      <button
                        key={d.fuente}
                        type="button"
                        onClick={() => openDetalleBalance("Egreso", d)}
                        className="w-full text-left px-4 py-3 border-b border-white/5 flex items-center justify-between hover:bg-red-500/10 transition-colors"
                      >
                        <div>
                          <span className={`text-sm font-medium ${FUENTE_COLOR[d.fuente] || "text-slate-300"}`}>{d.fuente}</span>
                          <span className="text-slate-500 text-xs ml-2">({d.cantidad})</span>
                        </div>
                        <span className="text-red-300 font-heading font-semibold text-sm">{fmtPYG(d.monto_pyg)}</span>
                      </button>
                    ))
                  )}
                </div>

              </div>

              {/* ── Panel USD ── */}
              {((balanceMes.total_ingresos_usd || 0) > 0 || (balanceMes.total_egresos_usd || 0) > 0) && (
                <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-amber-500/20">
                    <span className="text-amber-300 font-medium text-sm">💵 Movimientos en USD</span>
                    <span className="text-slate-500 text-xs ml-1">en moneda de origen</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-x divide-white/5">
                    {/* Ingresos USD */}
                    <div>
                      <div className="flex items-center justify-between px-4 py-2 border-b border-white/5 bg-emerald-500/5">
                        <span className="text-emerald-300 text-xs font-medium">Ingresos USD</span>
                        <span className="text-emerald-300 font-heading font-bold">
                          $ {Number(balanceMes.total_ingresos_usd || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                      {(balanceMes.ingresos_usd_detalle || []).length === 0 ? (
                        <p className="text-slate-600 text-xs text-center py-3">Sin ingresos USD</p>
                      ) : (
                        (balanceMes.ingresos_usd_detalle || []).map(d => (
                          <div key={d.fuente} className="px-4 py-2 border-b border-white/5 flex items-center justify-between">
                            <span className={`text-xs font-medium ${FUENTE_COLOR[d.fuente] || "text-slate-300"}`}>{d.fuente}</span>
                            <span className="text-emerald-300 text-xs font-heading">
                              $ {Number(d.monto_usd).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                    {/* Egresos USD */}
                    <div>
                      <div className="flex items-center justify-between px-4 py-2 border-b border-white/5 bg-red-500/5">
                        <span className="text-red-300 text-xs font-medium">Egresos USD</span>
                        <span className="text-red-300 font-heading font-bold">
                          $ {Number(balanceMes.total_egresos_usd || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                      {(balanceMes.egresos_usd_detalle || []).length === 0 ? (
                        <p className="text-slate-600 text-xs text-center py-3">Sin egresos USD</p>
                      ) : (
                        (balanceMes.egresos_usd_detalle || []).map(d => (
                          <div key={d.fuente} className="px-4 py-2 border-b border-white/5 flex items-center justify-between">
                            <span className={`text-xs font-medium ${FUENTE_COLOR[d.fuente] || "text-slate-300"}`}>{d.fuente}</span>
                            <span className="text-red-300 text-xs font-heading">
                              $ {Number(d.monto_usd).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  <div className="px-4 py-2 border-t border-amber-500/20 flex items-center justify-between">
                    <span className="text-slate-500 text-xs">Neto USD del mes</span>
                    <span className={`font-heading font-bold text-sm ${
                      (balanceMes.total_ingresos_usd || 0) - (balanceMes.total_egresos_usd || 0) >= 0
                        ? "text-emerald-300" : "text-red-300"
                    }`}>
                      $ {(((balanceMes.total_ingresos_usd || 0) - (balanceMes.total_egresos_usd || 0)) >= 0 ? "" : "−")}
                      {Math.abs((balanceMes.total_ingresos_usd || 0) - (balanceMes.total_egresos_usd || 0)).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              )}

              {/* ── Panel BRL ── */}
              {((balanceMes.total_ingresos_brl || 0) > 0 || (balanceMes.total_egresos_brl || 0) > 0) && (
                <div className="bg-green-500/5 border border-green-500/20 rounded-xl overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-green-500/20">
                    <span className="text-green-300 font-medium text-sm">🇧🇷 Movimientos en BRL</span>
                    <span className="text-slate-500 text-xs ml-1">en moneda de origen</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-x divide-white/5">
                    {/* Ingresos BRL */}
                    <div>
                      <div className="flex items-center justify-between px-4 py-2 border-b border-white/5 bg-emerald-500/5">
                        <span className="text-emerald-300 text-xs font-medium">Ingresos BRL</span>
                        <span className="text-emerald-300 font-heading font-bold">
                          R$ {Number(balanceMes.total_ingresos_brl || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                      {(balanceMes.ingresos_brl_detalle || []).length === 0 ? (
                        <p className="text-slate-600 text-xs text-center py-3">Sin ingresos BRL</p>
                      ) : (
                        (balanceMes.ingresos_brl_detalle || []).map(d => (
                          <div key={d.fuente} className="px-4 py-2 border-b border-white/5 flex items-center justify-between">
                            <span className={`text-xs font-medium ${FUENTE_COLOR[d.fuente] || "text-slate-300"}`}>{d.fuente}</span>
                            <span className="text-emerald-300 text-xs font-heading">
                              R$ {Number(d.monto_brl).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                    {/* Egresos BRL */}
                    <div>
                      <div className="flex items-center justify-between px-4 py-2 border-b border-white/5 bg-red-500/5">
                        <span className="text-red-300 text-xs font-medium">Egresos BRL</span>
                        <span className="text-red-300 font-heading font-bold">
                          R$ {Number(balanceMes.total_egresos_brl || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                      {(balanceMes.egresos_brl_detalle || []).length === 0 ? (
                        <p className="text-slate-600 text-xs text-center py-3">Sin egresos BRL</p>
                      ) : (
                        (balanceMes.egresos_brl_detalle || []).map(d => (
                          <div key={d.fuente} className="px-4 py-2 border-b border-white/5 flex items-center justify-between">
                            <span className={`text-xs font-medium ${FUENTE_COLOR[d.fuente] || "text-slate-300"}`}>{d.fuente}</span>
                            <span className="text-red-300 text-xs font-heading">
                              R$ {Number(d.monto_brl).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  <div className="px-4 py-2 border-t border-green-500/20 flex items-center justify-between">
                    <span className="text-slate-500 text-xs">Neto BRL del mes</span>
                    <span className={`font-heading font-bold text-sm ${
                      (balanceMes.total_ingresos_brl || 0) - (balanceMes.total_egresos_brl || 0) >= 0
                        ? "text-emerald-300" : "text-red-300"
                    }`}>
                      R$ {(((balanceMes.total_ingresos_brl || 0) - (balanceMes.total_egresos_brl || 0)) >= 0 ? "" : "−")}
                      {Math.abs((balanceMes.total_ingresos_brl || 0) - (balanceMes.total_egresos_brl || 0)).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              )}

            </div>
          )}

          {/* ══ VISTA ANUAL ══ */}
          {vista === "anual" && balanceAnual && (
            <div className="space-y-6">

              {/* Resumen anual */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
                  <p className="text-emerald-400 text-xs mb-1">Ingresos {anio}</p>
                  <p className="text-emerald-300 font-heading font-bold text-xl">{fmtPYG(balanceAnual.total_anual_ingresos)}</p>
                </div>
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                  <p className="text-red-400 text-xs mb-1">Egresos {anio}</p>
                  <p className="text-red-300 font-heading font-bold text-xl">{fmtPYG(balanceAnual.total_anual_egresos)}</p>
                </div>
                <div className={`border rounded-xl p-4 ${balanceAnual.balance_anual >= 0 ? "bg-teal-500/10 border-teal-500/20" : "bg-orange-500/10 border-orange-500/20"}`}>
                  <p className={`text-xs mb-1 ${balanceAnual.balance_anual >= 0 ? "text-teal-400" : "text-orange-400"}`}>Balance {anio}</p>
                  <BalanceChip valor={balanceAnual.balance_anual} />
                </div>
                <div className="bg-violet-500/10 border border-violet-500/20 rounded-xl p-4">
                  <p className="text-violet-400 text-xs mb-1">Saldo inicial bancos</p>
                  <p className="text-violet-300 font-heading font-bold text-xl">{fmtPYG(balanceAnual.superavit_inicial)}</p>
                </div>
              </div>

              {/* Tabla mensual */}
              <div className="bg-white/5 border border-white/10 rounded-xl overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Mes</th>
                      <th className="text-right text-slate-400 text-xs font-medium px-4 py-3">Ingresos</th>
                      <th className="text-right text-slate-400 text-xs font-medium px-4 py-3">Egresos</th>
                      <th className="text-right text-slate-400 text-xs font-medium px-4 py-3">Balance</th>
                      <th className="text-right text-slate-400 text-xs font-medium px-4 py-3">Acumulado</th>
                      <th className="px-4 py-3 w-28"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {balanceAnual.meses.map(m => {
                      const bal = m.balance;
                      const isCurrentMes = m.periodo === getMesActual();
                      return (
                        <tr key={m.periodo}
                          className={`border-b border-white/5 transition-colors ${isCurrentMes ? "bg-teal-500/5" : "hover:bg-white/3"}`}
                        >
                          <td className="px-4 py-3">
                            <span className={`text-sm font-medium ${isCurrentMes ? "text-teal-300" : "text-white"}`}>
                              {mesLabel(m.periodo)}
                            </span>
                            {isCurrentMes && <span className="ml-2 text-xs text-teal-500">◀ actual</span>}
                          </td>
                          <td className="px-4 py-3 text-right text-emerald-300 text-sm font-medium">
                            {m.total_ingresos ? fmtPYG(m.total_ingresos) : <span className="text-slate-600">—</span>}
                          </td>
                          <td className="px-4 py-3 text-right text-red-300 text-sm font-medium">
                            {m.total_egresos ? fmtPYG(m.total_egresos) : <span className="text-slate-600">—</span>}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {bal === 0 ? (
                              <span className="text-slate-600 text-sm">—</span>
                            ) : (
                              <span className={`text-sm font-heading font-semibold ${bal > 0 ? "text-teal-400" : "text-orange-400"}`}>
                                {bal > 0 ? "+" : ""}{fmtPYG(bal)}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className={`text-sm font-heading font-semibold ${m.acumulado >= 0 ? "text-violet-300" : "text-red-400"}`}>
                              {fmtPYG(m.acumulado)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <PropBar ingreso={m.total_ingresos} egreso={m.total_egresos} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-white/20 bg-white/5">
                      <td className="px-4 py-3 text-slate-300 text-sm font-medium">Total</td>
                      <td className="px-4 py-3 text-right text-emerald-300 font-heading font-bold">{fmtPYG(balanceAnual.total_anual_ingresos)}</td>
                      <td className="px-4 py-3 text-right text-red-300 font-heading font-bold">{fmtPYG(balanceAnual.total_anual_egresos)}</td>
                      <td className="px-4 py-3 text-right">
                        <BalanceChip valor={balanceAnual.balance_anual} />
                      </td>
                      <td className="px-4 py-3" colSpan={2}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

            </div>
          )}

          {/* ══ VISTA GERENCIAL ══ */}
          {vista === "gerencial" && (
            <div className="space-y-6">
              {loadingGerencial ? (
                <div className="text-slate-500 text-center py-16">Calculando Balance Gerencial...</div>
              ) : !balanceGerencial ? (
                <div className="text-slate-500 text-center py-16">Sin datos para este período</div>
              ) : (
                <>
                  <div className="bg-violet-500/10 border border-violet-500/20 rounded-xl p-4 text-xs text-violet-200">
                    Balance Analítico Gerencial (uso interno, no fiscal) — método del devengado. Cálculo en vivo: no reemplaza a la contabilidad formal ni a tus obligaciones ante la SET/DNIT.
                  </div>

                  <div className="flex justify-end">
                    <button onClick={() => setShowConfigGerencial(v => !v)}
                      className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-1.5 rounded-lg transition-colors">
                      <Settings className="w-3.5 h-3.5" /> Capital Social y Bienes de Uso
                    </button>
                  </div>

                  {showConfigGerencial && (
                    <form onSubmit={handleSaveConfigGerencial} className="bg-white/5 border border-white/10 rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-slate-400 text-xs mb-1 block">Capital Social</label>
                        <div className="flex gap-2">
                          <input type="number" min="0" value={configGerencialForm.capital_social}
                            onChange={e => setConfigGerencialForm(f => ({ ...f, capital_social: e.target.value }))}
                            className="flex-1 bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-violet-500/50" />
                          <select value={configGerencialForm.capital_social_moneda}
                            onChange={e => setConfigGerencialForm(f => ({ ...f, capital_social_moneda: e.target.value }))}
                            className="bg-arandu-dark border border-white/10 text-white rounded-lg px-2 py-2 text-sm focus:outline-none">
                            <option value="PYG">PYG</option><option value="USD">USD</option><option value="BRL">BRL</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="text-slate-400 text-xs mb-1 block">Bienes de Uso (valor neto)</label>
                        <div className="flex gap-2">
                          <input type="number" min="0" value={configGerencialForm.bienes_de_uso}
                            onChange={e => setConfigGerencialForm(f => ({ ...f, bienes_de_uso: e.target.value }))}
                            className="flex-1 bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-violet-500/50" />
                          <select value={configGerencialForm.bienes_de_uso_moneda}
                            onChange={e => setConfigGerencialForm(f => ({ ...f, bienes_de_uso_moneda: e.target.value }))}
                            className="bg-arandu-dark border border-white/10 text-white rounded-lg px-2 py-2 text-sm focus:outline-none">
                            <option value="PYG">PYG</option><option value="USD">USD</option><option value="BRL">BRL</option>
                          </select>
                        </div>
                      </div>
                      <div className="md:col-span-2 flex gap-2">
                        <button type="submit" disabled={savingConfigGerencial}
                          className="px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white rounded-lg text-sm font-medium transition-colors">
                          {savingConfigGerencial ? "Guardando..." : "Guardar"}
                        </button>
                        <button type="button" onClick={() => setShowConfigGerencial(false)}
                          className="px-4 py-2 bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg text-sm transition-colors">
                          Cancelar
                        </button>
                      </div>
                    </form>
                  )}

                  {/* Diferencia de cuadre — diagnóstico transparente */}
                  <div className={`rounded-xl p-4 border ${balanceGerencial.cuadre.cuadra ? "bg-emerald-500/10 border-emerald-500/20" : "bg-amber-500/10 border-amber-500/20"}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <p className={`text-xs uppercase tracking-wide font-semibold ${balanceGerencial.cuadre.cuadra ? "text-emerald-300" : "text-amber-300"}`}>
                        Diferencia de cuadre (diagnóstico)
                      </p>
                      <p className={`font-heading text-lg font-bold ${balanceGerencial.cuadre.cuadra ? "text-emerald-300" : "text-amber-300"}`}>
                        {fmtPYG(balanceGerencial.cuadre.diferencia)}
                      </p>
                    </div>
                    <p className="text-slate-400 text-xs mt-1">
                      Activo Total {fmtPYG(balanceGerencial.cuadre.activo_total)} vs. Pasivo + Patrimonio {fmtPYG(balanceGerencial.cuadre.pasivo_mas_patrimonio)}.
                      Este sistema no usa partida doble contable, así que una diferencia no es necesariamente un error — puede reflejar IVA, retenciones u otros efectos que este reporte gerencial no modela.
                    </p>
                  </div>

                  {/* ACTIVO / PASIVO + PATRIMONIO */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                      <h3 className="text-emerald-300 font-heading font-bold text-sm mb-3 uppercase tracking-wide">Activo</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-slate-400">Disponibilidades (bancos/caja)</span><span className="text-white">{fmtPYG(balanceGerencial.activo.disponibilidades)}</span></div>
                        <div className="flex justify-between"><span className="text-slate-400">Cuentas por Cobrar</span><span className="text-white">{fmtPYG(balanceGerencial.activo.cuentas_por_cobrar)}</span></div>
                        <div className="flex justify-between"><span className="text-slate-400">Inventarios</span><span className="text-white">{fmtPYG(balanceGerencial.activo.inventarios)}</span></div>
                        <div className="flex justify-between"><span className="text-slate-400">Bienes de Uso</span><span className="text-white">{fmtPYG(balanceGerencial.activo.bienes_de_uso)}</span></div>
                      </div>
                      <div className="flex justify-between mt-3 pt-3 border-t border-white/10">
                        <span className="text-emerald-300 font-semibold text-sm">Activo Total</span>
                        <span className="text-emerald-300 font-heading font-bold">{fmtPYG(balanceGerencial.activo.total)}</span>
                      </div>
                    </div>

                    <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                      <h3 className="text-red-300 font-heading font-bold text-sm mb-3 uppercase tracking-wide">Pasivo</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-slate-400">Proveedores a pagar</span><span className="text-white">{fmtPYG(balanceGerencial.pasivo.proveedores)}</span></div>
                        <div className="flex justify-between"><span className="text-slate-400">Obligaciones Pendientes</span><span className="text-white">{fmtPYG(balanceGerencial.pasivo.obligaciones_pendientes)}</span></div>
                        <div className="pl-3 space-y-1 text-xs text-slate-500">
                          <div className="flex justify-between"><span>Costos fijos impagos</span><span>{fmtPYG(balanceGerencial.pasivo.obligaciones_detalle.costos_fijos_impagos)}</span></div>
                          <div className="flex justify-between"><span>Sueldos impagos</span><span>{fmtPYG(balanceGerencial.pasivo.obligaciones_detalle.sueldos_impagos)}</span></div>
                          <div className="flex justify-between"><span>Aporte Patronal IPS (16.5%)</span><span>{fmtPYG(balanceGerencial.pasivo.obligaciones_detalle.aporte_patronal_ips)}</span></div>
                          <div className="flex justify-between"><span>Provisión Aguinaldo (1/12)</span><span>{fmtPYG(balanceGerencial.pasivo.obligaciones_detalle.provision_aguinaldo)}</span></div>
                        </div>
                      </div>
                      <div className="flex justify-between mt-3 pt-3 border-t border-white/10">
                        <span className="text-red-300 font-semibold text-sm">Pasivo Total</span>
                        <span className="text-red-300 font-heading font-bold">{fmtPYG(balanceGerencial.pasivo.total)}</span>
                      </div>

                      <h3 className="text-teal-300 font-heading font-bold text-sm mt-5 mb-3 uppercase tracking-wide">Patrimonio Neto</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-slate-400">Capital Social</span><span className="text-white">{fmtPYG(balanceGerencial.patrimonio.capital_social)}</span></div>
                        <div className="flex justify-between"><span className="text-slate-400">Resultado del Ejercicio</span>
                          <span className={balanceGerencial.patrimonio.resultado_del_ejercicio >= 0 ? "text-emerald-300" : "text-red-300"}>{fmtPYG(balanceGerencial.patrimonio.resultado_del_ejercicio)}</span>
                        </div>
                      </div>
                      <div className="flex justify-between mt-3 pt-3 border-t border-white/10">
                        <span className="text-teal-300 font-semibold text-sm">Patrimonio Neto Total</span>
                        <span className="text-teal-300 font-heading font-bold">{fmtPYG(balanceGerencial.patrimonio.total)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Sub-reporte de Resultados del período */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                    <h3 className="text-white font-heading font-bold text-sm mb-3">Resultados del período — {mesLabel(periodo)}</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      <div><p className="text-slate-500 text-xs">Ventas Totales</p><p className="text-white font-semibold">{fmtPYG(balanceGerencial.resultados.ventas_totales)}</p></div>
                      <div><p className="text-slate-500 text-xs">Costo de Mercadería Vendida</p><p className="text-white font-semibold">{fmtPYG(balanceGerencial.resultados.cmv)}</p></div>
                      <div><p className="text-slate-500 text-xs">Utilidad Bruta</p><p className="text-emerald-300 font-semibold">{fmtPYG(balanceGerencial.resultados.utilidad_bruta)}</p></div>
                      <div><p className="text-slate-500 text-xs">Gastos Operativos</p><p className="text-white font-semibold">{fmtPYG(balanceGerencial.resultados.gastos_operativos)}</p></div>
                      <div><p className="text-slate-500 text-xs">Costo de Personal</p><p className="text-white font-semibold">{fmtPYG(balanceGerencial.resultados.costo_personal)}</p></div>
                      <div><p className="text-slate-500 text-xs">Resultado Neto</p>
                        <p className={`font-bold ${balanceGerencial.resultados.resultado_neto >= 0 ? "text-emerald-300" : "text-red-300"}`}>{fmtPYG(balanceGerencial.resultados.resultado_neto)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Cuadro de mando — KPIs con semáforo */}
                  <div>
                    <h3 className="text-white font-heading font-bold text-sm mb-3">Cuadro de mando</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {Object.entries(balanceGerencial.kpis).map(([key, kpi]) => {
                        const colores = {
                          verde: { bg: "bg-emerald-500/10", border: "border-emerald-500/30", text: "text-emerald-300", dot: "bg-emerald-400" },
                          amarillo: { bg: "bg-amber-500/10", border: "border-amber-500/30", text: "text-amber-300", dot: "bg-amber-400" },
                          rojo: { bg: "bg-red-500/10", border: "border-red-500/30", text: "text-red-300", dot: "bg-red-400" },
                        };
                        const c = colores[kpi.semaforo] || colores.amarillo;
                        const labels = {
                          independencia_financiera: "% Independencia Financiera",
                          endeudamiento: "% Endeudamiento",
                          margen_bruto: "% Margen Bruto",
                          margen_neto: "% Margen Neto",
                          liquidez_inmediata: "% Liquidez Inmediata",
                          morosidad: "% Morosidad",
                        };
                        return (
                          <div key={key} className={`rounded-xl p-4 border ${c.bg} ${c.border}`}>
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`w-2 h-2 rounded-full ${c.dot}`} />
                              <p className="text-slate-300 text-xs font-medium">{labels[key] || key}</p>
                            </div>
                            <p className={`font-heading text-2xl font-bold ${c.text}`}>{kpi.valor}%</p>
                            <p className="text-slate-500 text-[11px] mt-1">{kpi.descripcion}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ══ VISTA IVA FISCAL ══ */}
          {vista === "iva" && (
            <div className="space-y-6">
              {/* Toggle mensual / anual */}
              <div className="flex gap-1 bg-white/5 rounded-xl p-1 w-fit">
                {[
                  { key: "mensual", label: "Por mes" },
                  { key: "anual", label: "Por año" },
                  { key: "todo", label: "Todo el tiempo" },
                ].map(v => (
                  <button key={v.key} onClick={() => setVistaIvaMode(v.key)}
                    className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${vistaIvaMode === v.key ? "bg-amber-600 text-white shadow" : "text-slate-400 hover:text-white"}`}>
                    {v.label}
                  </button>
                ))}
              </div>

              {/* VISTA MENSUAL */}
              {vistaIvaMode === "mensual" && (
                <>
                  {loadingIva ? (
                    <div className="text-slate-500 text-center py-16">Calculando IVA fiscal...</div>
                  ) : ivaData ? (
                    <>
                      {/* Cards resumen */}
                      {(() => {
                        // Saldo actual: mismo cálculo que usa Egresos > IVA ("Saldo IVA
                        // acumulado"), tomado directo del backend (saldo_pendiente_acumulado /
                        // saldo_a_favor_acumulado — netting real desde enero, no un piso por
                        // mes) para que ambas pantallas siempre muestren el mismo número.
                        const saldoActualIva = ivaData.saldo_pendiente_acumulado || 0;
                        const saldoFavorIva = ivaData.saldo_a_favor_acumulado || 0;
                        return (
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                        <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-xl p-5">
                          <p className="text-cyan-400 text-xs mb-1">IVA Débito Fiscal</p>
                          <p className="text-xs text-slate-500 mb-2">De facturas emitidas</p>
                          <p className="text-cyan-300 font-heading font-bold text-2xl">{fmtPYG(ivaData.iva_debito)}</p>
                        </div>
                        <div className="bg-violet-500/10 border border-violet-500/20 rounded-xl p-5">
                          <p className="text-violet-400 text-xs mb-1">IVA Crédito Fiscal</p>
                          <p className="text-violet-300 font-heading font-bold text-2xl">{fmtPYG(ivaData.iva_credito)}</p>
                          {ivaData.iva_credito_retenciones > 0 && (
                            <div className="mt-2 space-y-0.5">
                              <p className="text-xs text-slate-500">Compras: {fmtPYG(ivaData.iva_credito_compras)}</p>
                              <p className="text-xs text-amber-400">+ Retenciones: {fmtPYG(ivaData.iva_credito_retenciones)}</p>
                            </div>
                          )}
                          {!ivaData.iva_credito_retenciones && (
                            <p className="text-xs text-slate-500 mt-1">De compras con factura</p>
                          )}
                        </div>
                        <div className={`rounded-xl p-5 border ${ivaData.a_favor ? "bg-emerald-500/10 border-emerald-500/20" : "bg-red-500/10 border-red-500/20"}`}>
                          <p className={`text-xs mb-1 ${ivaData.a_favor ? "text-emerald-400" : "text-red-400"}`}>IVA Neto del mes</p>
                          <p className="text-xs text-slate-500 mb-2">{ivaData.a_favor ? "A favor del contribuyente" : "A pagar a la SET"}</p>
                          <p className={`font-heading font-bold text-2xl ${ivaData.a_favor ? "text-emerald-300" : "text-red-300"}`}>
                            {fmtPYG(Math.abs(ivaData.iva_neto))}
                          </p>
                          <span className={`inline-block mt-2 px-2 py-0.5 rounded text-xs font-semibold ${ivaData.a_favor ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"}`}>
                            {ivaData.a_favor ? "✓ A favor" : "⚠ A pagar"}
                          </span>
                        </div>
                        <div className={`rounded-xl p-5 border ${saldoActualIva > 0 ? "bg-red-500/10 border-red-500/20" : saldoFavorIva > 0 ? "bg-emerald-500/10 border-emerald-500/20" : "bg-slate-500/10 border-slate-500/20"}`}>
                          <p className={`text-xs mb-1 ${saldoActualIva > 0 ? "text-red-400" : saldoFavorIva > 0 ? "text-emerald-400" : "text-slate-400"}`}>Saldo actual</p>
                          <p className="text-xs text-slate-500 mb-2">
                            {saldoActualIva > 0 ? "Deuda pendiente con la SET" : saldoFavorIva > 0 ? "A favor del contribuyente" : "IVA al día"}
                          </p>
                          <p className={`font-heading font-bold text-2xl ${saldoActualIva > 0 ? "text-red-300" : saldoFavorIva > 0 ? "text-emerald-300" : "text-slate-300"}`}>
                            {saldoActualIva > 0
                              ? fmtPYG(saldoActualIva)
                              : saldoFavorIva > 0
                                ? fmtPYG(saldoFavorIva)
                                : <span className="text-emerald-300">✓ Al día</span>}
                          </p>
                          {saldoActualIva > 0 && (
                            <span className="inline-block mt-2 px-2 py-0.5 rounded text-xs font-semibold bg-red-500/20 text-red-400">⚠ A pagar</span>
                          )}
                          {saldoActualIva === 0 && saldoFavorIva > 0 && (
                            <span className="inline-block mt-2 px-2 py-0.5 rounded text-xs font-semibold bg-emerald-500/20 text-emerald-400">✓ A favor</span>
                          )}
                        </div>
                        {/* Total pagado a SET en el año */}
                        <div className="rounded-xl p-5 border bg-slate-500/10 border-slate-500/20">
                          <p className="text-xs mb-1 text-slate-400">
                            Pagado a SET ({new Date(ivaData.periodo + "-01").getFullYear()})
                          </p>
                          <p className="text-xs text-slate-500 mb-2">Total pagos directos realizados</p>
                          <p className="font-heading font-bold text-2xl text-slate-200">
                            {fmtPYG(ivaData.iva_pagado_acumulado || 0)}
                          </p>
                          {(ivaData.iva_pagado_acumulado || 0) === 0 && (
                            <p className="text-slate-600 text-xs mt-1">Sin pagos registrados</p>
                          )}
                        </div>
                      </div>
                        );
                      })()}

                      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between gap-2">
                          <span className="text-amber-400 font-medium text-sm">Pagos directos a la SET ({mesLabel(ivaData.periodo)})</span>
                          <span className="text-amber-300 font-heading font-bold text-sm">{fmtPYG(ivaData.pagos_iva_mes || 0)}</span>
                        </div>
                        {!ivaData.pagos_iva_detalle || ivaData.pagos_iva_detalle.length === 0 ? (
                          <p className="text-slate-500 text-sm text-center py-5">Sin pagos registrados para este IVA</p>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full min-w-[620px] text-sm">
                              <thead>
                                <tr className="border-b border-white/10">
                                  <th className="text-left text-slate-400 text-xs font-medium px-4 py-2">Fecha pago</th>
                                  <th className="text-left text-slate-400 text-xs font-medium px-4 py-2">Descripcion</th>
                                  <th className="text-left text-slate-400 text-xs font-medium px-4 py-2">Cuenta</th>
                                  <th className="text-right text-slate-400 text-xs font-medium px-4 py-2">Monto</th>
                                </tr>
                              </thead>
                              <tbody>
                                {ivaData.pagos_iva_detalle.map((p, i) => (
                                  <tr key={p.id || i} className="border-b border-white/5 hover:bg-white/3">
                                    <td className="px-4 py-2 text-slate-300">{p.fecha_pago || "-"}</td>
                                    <td className="px-4 py-2">
                                      <p className="text-white">{p.descripcion || `Pago IVA ${p.periodo_iva || ivaData.periodo}`}</p>
                                      {p.notas && <p className="text-slate-500 text-xs">{p.notas}</p>}
                                      {p.legacy && <p className="text-amber-500 text-xs">Registro anterior</p>}
                                    </td>
                                    <td className="px-4 py-2 text-slate-400">{p.cuenta_nombre || "-"}</td>
                                    <td className="px-4 py-2 text-right text-amber-300 font-medium">{fmtPYG(p.monto || 0)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>

                      {/* Historial de IVA por mes (mini tabla) */}
                      {ivaData.historial_meses && ivaData.historial_meses.length > 1 && (
                        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
                            <span className="text-amber-400 font-medium text-sm">📊 Historial IVA acumulado (enero → {mesLabel(ivaData.periodo)})</span>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full min-w-[400px] text-sm">
                              <thead>
                                <tr className="border-b border-white/10">
                                  <th className="text-left text-slate-400 text-xs font-medium px-4 py-2">Mes</th>
                                  <th className="text-right text-slate-400 text-xs font-medium px-4 py-2">Débito</th>
                                  <th className="text-right text-slate-400 text-xs font-medium px-4 py-2">Crédito</th>
                                  <th className="text-right text-slate-400 text-xs font-medium px-4 py-2">Neto</th>
                                  <th className="text-right text-slate-400 text-xs font-medium px-4 py-2">Pagado</th>
                                </tr>
                              </thead>
                              <tbody>
                                {ivaData.historial_meses.map((h, i) => {
                                  const isActual = h.periodo === ivaData.periodo;
                                  // Mes con deuda sin cubrir con pago SET directo
                                  const deudaSinPago = h.neto > 0 && (h.pagos_iva || 0) < h.neto;
                                  return (
                                    <tr key={i} className={`border-b border-white/5 ${isActual ? "bg-amber-500/5" : deudaSinPago ? "bg-orange-500/3" : "hover:bg-white/3"}`}>
                                      <td className={`px-4 py-2 text-sm font-medium ${isActual ? "text-amber-300" : "text-white"}`}>
                                        {mesLabel(h.periodo)}{isActual && <span className="ml-2 text-xs text-amber-500">◀</span>}
                                      </td>
                                      <td className="px-4 py-2 text-right text-cyan-300 text-sm">{h.debito ? fmtPYG(h.debito) : <span className="text-slate-600">—</span>}</td>
                                      <td className="px-4 py-2 text-right text-violet-300 text-sm">{h.credito ? fmtPYG(h.credito) : <span className="text-slate-600">—</span>}</td>
                                      <td className="px-4 py-2 text-right">
                                        {h.neto === 0 ? <span className="text-slate-600 text-sm">—</span> : (
                                          <span className={`text-sm font-semibold ${h.neto > 0 ? "text-red-400" : "text-emerald-400"}`}>
                                            {h.neto > 0 ? "A pagar " : "A favor "}{fmtPYG(Math.abs(h.neto))}
                                          </span>
                                        )}
                                      </td>
                                      <td className="px-4 py-2 text-right text-slate-400 text-sm">
                                        {h.pagos_iva ? fmtPYG(h.pagos_iva) : <span className="text-slate-600">—</span>}
                                        {deudaSinPago && (
                                          <span className="ml-2 inline-block px-1.5 py-0.5 rounded text-xs font-semibold bg-orange-500/20 text-orange-400">
                                            {(h.pagos_iva || 0) > 0 ? "Parcial" : "Sin pago"}
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Detalle débito */}
                      {ivaData.detalle_debito && ivaData.detalle_debito.length > 0 && (
                        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
                            <span className="text-cyan-400 font-medium text-sm">📄 Facturas Emitidas (Débito Fiscal)</span>
                            <span className="ml-auto text-cyan-300 font-heading font-bold text-sm">{fmtPYG(ivaData.iva_debito)}</span>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full min-w-[500px] text-sm">
                              <thead>
                                <tr className="border-b border-white/10">
                                  <th className="text-left text-slate-400 text-xs font-medium px-4 py-2">Nº / Empresa</th>
                                  <th className="text-right text-slate-400 text-xs font-medium px-4 py-2">Monto</th>
                                  <th className="text-right text-slate-400 text-xs font-medium px-4 py-2">IVA</th>
                                  <th className="text-right text-slate-400 text-xs font-medium px-4 py-2">Tasa</th>
                                </tr>
                              </thead>
                              <tbody>
                                {ivaData.detalle_debito.map((d, i) => (
                                  <tr key={i} className="border-b border-white/5 hover:bg-white/3">
                                    <td className="px-4 py-2">
                                      <p className="text-white flex items-center gap-2">
                                        {d.numero || d.descripcion || "—"}
                                        {d.tipo_documento === "nota_credito" && (
                                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 border border-rose-500/30">Nota crédito</span>
                                        )}
                                      </p>
                                      {d.empresa_nombre && <p className="text-slate-500 text-xs">{d.empresa_nombre}</p>}
                                      {d.efecto === "resta_debito" && <p className="text-rose-300 text-xs">Resta débito fiscal</p>}
                                    </td>
                                    <td className={`px-4 py-2 text-right ${d.monto < 0 ? "text-rose-300" : "text-slate-300"}`}>{fmtPYG(d.monto)}</td>
                                    <td className={`px-4 py-2 text-right font-medium ${d.iva < 0 ? "text-rose-300" : "text-cyan-300"}`}>{fmtPYG(d.iva)}</td>
                                    <td className="px-4 py-2 text-right text-slate-400 text-xs">{d.tasa ? (typeof d.tasa === "number" ? `${d.tasa}%` : d.tasa) : "—"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Detalle crédito */}
                      {ivaData.detalle_credito && ivaData.detalle_credito.length > 0 && (
                        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
                            <span className="text-violet-400 font-medium text-sm">🛒 Compras con Factura (Crédito Fiscal)</span>
                            <span className="ml-auto text-violet-300 font-heading font-bold text-sm">{fmtPYG(ivaData.iva_credito)}</span>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full min-w-[500px] text-sm">
                              <thead>
                                <tr className="border-b border-white/10">
                                  <th className="text-left text-slate-400 text-xs font-medium px-4 py-2">Descripción / Proveedor</th>
                                  <th className="text-right text-slate-400 text-xs font-medium px-4 py-2">Monto</th>
                                  <th className="text-right text-slate-400 text-xs font-medium px-4 py-2">IVA</th>
                                  <th className="text-right text-slate-400 text-xs font-medium px-4 py-2">Tasa</th>
                                </tr>
                              </thead>
                              <tbody>
                                {ivaData.detalle_credito.map((d, i) => (
                                  <tr key={i} className="border-b border-white/5 hover:bg-white/3">
                                    <td className="px-4 py-2">
                                      <p className="text-white flex items-center gap-2">
                                        {d.descripcion || "—"}
                                        {d.tipo_documento === "nota_credito_compra" && (
                                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 border border-rose-500/30">Nota crédito</span>
                                        )}
                                      </p>
                                      {d.proveedor_nombre && <p className="text-slate-500 text-xs">{d.proveedor_nombre}</p>}
                                      {d.efecto === "resta_credito" && <p className="text-rose-300 text-xs">Resta crédito fiscal</p>}
                                    </td>
                                    <td className={`px-4 py-2 text-right ${d.monto < 0 ? "text-rose-300" : "text-slate-300"}`}>{fmtPYG(d.monto)}</td>
                                    <td className={`px-4 py-2 text-right font-medium ${d.iva < 0 ? "text-rose-300" : "text-violet-300"}`}>{fmtPYG(d.iva)}</td>
                                    <td className="px-4 py-2 text-right text-slate-400 text-xs">{d.tasa ? (typeof d.tasa === "number" ? `${d.tasa}%` : d.tasa) : "—"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Detalle retenciones */}
                      {ivaData.detalle_retenciones && ivaData.detalle_retenciones.length > 0 && (
                        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
                            <span className="text-amber-400 font-medium text-sm">🔒 Retenciones de IVA (Clientes que retienen)</span>
                            <span className="ml-auto text-amber-300 font-heading font-bold text-sm">{fmtPYG(ivaData.iva_credito_retenciones)}</span>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full min-w-[500px] text-sm">
                              <thead>
                                <tr className="border-b border-white/10">
                                  <th className="text-left text-slate-400 text-xs font-medium px-4 py-2">Cliente</th>
                                  <th className="text-right text-slate-400 text-xs font-medium px-4 py-2">Factura</th>
                                  <th className="text-right text-slate-400 text-xs font-medium px-4 py-2">IVA factura</th>
                                  <th className="text-right text-slate-400 text-xs font-medium px-4 py-2">% Ret.</th>
                                  <th className="text-right text-slate-400 text-xs font-medium px-4 py-2">Retenido</th>
                                </tr>
                              </thead>
                              <tbody>
                                {ivaData.detalle_retenciones.map((r, i) => (
                                  <tr key={i} className="border-b border-white/5 hover:bg-white/3">
                                    <td className="px-4 py-2 text-white">{r.razon_social || "—"}</td>
                                    <td className="px-4 py-2 text-right text-slate-400 text-xs">{r.numero_factura || "—"}</td>
                                    <td className="px-4 py-2 text-right text-slate-300">{fmtPYG(r.iva_factura)}</td>
                                    <td className="px-4 py-2 text-right text-slate-400 text-xs">{r.porcentaje_retencion}%</td>
                                    <td className="px-4 py-2 text-right text-amber-300 font-medium">{fmtPYG(r.monto_retenido)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <div className="px-4 py-2 text-xs text-slate-500 border-t border-white/5">
                            Las retenciones de IVA aplicadas por clientes se suman al crédito fiscal del período
                          </div>
                        </div>
                      )}

                      {ivaData.detalle_debito?.length === 0 && ivaData.detalle_credito?.length === 0 && (!ivaData.detalle_retenciones || ivaData.detalle_retenciones.length === 0) && (
                        <div className="text-center py-12 text-slate-500">No hay movimientos de IVA para este período</div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-12 text-slate-500">Sin datos de IVA para este período</div>
                  )}
                </>
              )}

              {/* VISTA ANUAL / TODO */}
              {(vistaIvaMode === "anual" || vistaIvaMode === "todo") && (
                <div className="space-y-4">
                  {loadingIvaAnual ? (
                    <div className="text-slate-500 text-center py-12">Calculando IVA...</div>
                  ) : (
                    <>
                      {/* Summary cards for year */}
                      {(() => {
                        const totalDebito = ivaResumen?.total_debito ?? ivaAnualData.reduce((s, m) => s + m.iva_debito, 0);
                        const totalCredito = ivaResumen?.total_credito ?? ivaAnualData.reduce((s, m) => s + m.iva_credito, 0);
                        const totalPagado = ivaResumen?.total_pagado ?? ivaAnualData.reduce((s, m) => s + (m.pagos_iva || 0), 0);
                        const totalNeto = ivaResumen?.total_neto ?? ivaAnualData.reduce((s, m) => s + m.iva_neto, 0);
                        const saldoActual = totalNeto - totalPagado;
                        return (
                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
                        {[
                          { label: `Débito fiscal ${vistaIvaMode === "todo" ? "total" : anio}`, value: fmtPYG(totalDebito), color: "text-cyan-300", bg: "bg-cyan-500/10 border-cyan-500/20" },
                          { label: `Crédito fiscal ${vistaIvaMode === "todo" ? "total" : anio}`, value: fmtPYG(totalCredito), color: "text-violet-300", bg: "bg-violet-500/10 border-violet-500/20" },
                          { label: `IVA neto ${vistaIvaMode === "todo" ? "total" : anio}`, value: fmtPYG(Math.abs(totalNeto)), color: totalNeto > 0 ? "text-red-300" : "text-emerald-300", bg: totalNeto > 0 ? "bg-red-500/10 border-red-500/20" : "bg-emerald-500/10 border-emerald-500/20" },
                          { label: `Pagado SET ${vistaIvaMode === "todo" ? "total" : anio}`, value: fmtPYG(totalPagado), color: "text-amber-300", bg: "bg-amber-500/10 border-amber-500/20" },
                          { label: "Saldo actual", value: fmtPYG(Math.abs(saldoActual)), sub: saldoActual > 0 ? "Debe SET" : saldoActual < 0 ? "A favor" : "Al día", color: saldoActual > 0 ? "text-red-300" : saldoActual < 0 ? "text-emerald-300" : "text-slate-300", bg: saldoActual > 0 ? "bg-red-500/10 border-red-500/20" : saldoActual < 0 ? "bg-emerald-500/10 border-emerald-500/20" : "bg-slate-500/10 border-slate-500/20" },
                        ].map(card => (
                          <div key={card.label} className={`rounded-xl p-4 border ${card.bg}`}>
                            <p className="text-slate-400 text-xs mb-1">{card.label}</p>
                            <p className={`font-heading font-bold text-xl ${card.color}`}>{card.value}</p>
                            {card.sub && <p className="text-slate-500 text-xs mt-1">{card.sub}</p>}
                          </div>
                        ))}
                      </div>
                        );
                      })()}
                      {/* Monthly IVA table */}
                      <div className="bg-white/5 border border-white/10 rounded-xl overflow-x-auto">
                        <table className="w-full min-w-[500px]">
                          <thead>
                            <tr className="border-b border-white/10">
                              <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Mes</th>
                              <th className="text-right text-slate-400 text-xs font-medium px-4 py-3">Débito</th>
                              <th className="text-right text-slate-400 text-xs font-medium px-4 py-3">Crédito</th>
                              <th className="text-right text-slate-400 text-xs font-medium px-4 py-3">IVA neto</th>
                              <th className="text-right text-slate-400 text-xs font-medium px-4 py-3">Pagado</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ivaAnualData.map((m, i) => {
                              const isCurrentMes = m.periodo === getMesActual();
                              const neto = m.iva_neto || 0;
                              return (
                                <tr
                                  key={m.periodo}
                                  onClick={() => { setPeriodo(m.periodo); setVistaIvaMode("mensual"); }}
                                  className={`border-b border-white/5 cursor-pointer ${isCurrentMes ? "bg-amber-500/5" : "hover:bg-white/3"}`}
                                >
                                  <td className="px-4 py-3">
                                    <span className={`text-sm font-medium ${isCurrentMes ? "text-amber-300" : "text-white"}`}>
                                      {mesLabel(m.periodo)}
                                    </span>
                                    {isCurrentMes && <span className="ml-2 text-xs text-amber-500">◀ actual</span>}
                                  </td>
                                  <td className="px-4 py-3 text-right text-cyan-300 text-sm">{m.iva_debito ? fmtPYG(m.iva_debito) : <span className="text-slate-600">—</span>}</td>
                                  <td className="px-4 py-3 text-right text-violet-300 text-sm">{m.iva_credito ? fmtPYG(m.iva_credito) : <span className="text-slate-600">—</span>}</td>
                                  <td className="px-4 py-3 text-right">
                                    {neto === 0 ? <span className="text-slate-600 text-sm">—</span> : (
                                      <span className={`text-sm font-heading font-semibold ${neto > 0 ? "text-red-400" : "text-emerald-400"}`}>
                                        {neto > 0 ? "A pagar " : "A favor "}{fmtPYG(Math.abs(neto))}
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-right text-amber-300 text-sm">
                                    {m.pagos_iva ? fmtPYG(m.pagos_iva) : <span className="text-slate-600">—</span>}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr className="border-t border-white/20 bg-white/5">
                              <td className="px-4 py-3 text-slate-300 text-sm font-medium">Total {vistaIvaMode === "todo" ? "general" : anio}</td>
                              <td className="px-4 py-3 text-right text-cyan-300 font-heading font-bold">{fmtPYG(ivaAnualData.reduce((s, m) => s + m.iva_debito, 0))}</td>
                              <td className="px-4 py-3 text-right text-violet-300 font-heading font-bold">{fmtPYG(ivaAnualData.reduce((s, m) => s + m.iva_credito, 0))}</td>
                              <td className="px-4 py-3 text-right">
                                {(() => { const n = ivaAnualData.reduce((s, m) => s + m.iva_neto, 0); return (
                                  <span className={`font-heading font-bold text-sm ${n > 0 ? "text-red-400" : "text-emerald-400"}`}>
                                    {n > 0 ? "A pagar " : "A favor "}{fmtPYG(Math.abs(n))}
                                  </span>
                                ); })()}
                              </td>
                              <td className="px-4 py-3 text-right text-amber-300 font-heading font-bold">{fmtPYG(ivaAnualData.reduce((s, m) => s + (m.pagos_iva || 0), 0))}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          </>
        )}
      </div>

      {/* ══ MODAL DETALLE BALANCE ══ */}
      {detalleBalance && (
        <div
          className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        >
          <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-start justify-between gap-4 p-5 border-b border-white/10">
              <div>
                <p className={`text-xs font-medium mb-1 ${detalleBalance.tipo === "Ingreso" ? "text-emerald-400" : "text-red-400"}`}>
                  {detalleBalance.tipo} · {mesLabel(periodo)}
                </p>
                <h2 className="text-white font-heading font-bold text-xl">{detalleBalance.fuente}</h2>
                <p className="text-slate-500 text-sm mt-1">
                  {detalleBalance.cantidad || 0} registro{Number(detalleBalance.cantidad || 0) !== 1 ? "s" : ""} · total del grupo {fmtPYG(detalleBalance.monto_pyg || 0)}
                </p>
              </div>
              <button onClick={() => setDetalleBalance(null)} className="text-slate-400 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 border-b border-white/10">
              <input
                value={detalleSearch}
                onChange={e => setDetalleSearch(e.target.value)}
                placeholder="Buscar por fecha, persona/proveedor, documento, cuenta o descripción..."
                className="w-full bg-arandu-dark border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500/60 placeholder-slate-600"
              />
            </div>

            <div className="overflow-auto">
              {detalleItems.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-10">Sin registros para este filtro</p>
              ) : (
                <table className="w-full min-w-[850px]">
                  <thead className="sticky top-0 bg-arandu-dark-light border-b border-white/10">
                    <tr>
                      <th className="text-left text-slate-400 text-xs font-medium px-5 py-3">Fecha</th>
                      <th className="text-left text-slate-400 text-xs font-medium px-5 py-3">Persona / Proveedor / Cliente</th>
                      <th className="text-left text-slate-400 text-xs font-medium px-5 py-3">Detalle</th>
                      <th className="text-left text-slate-400 text-xs font-medium px-5 py-3">Cuenta</th>
                      <th className="text-right text-slate-400 text-xs font-medium px-5 py-3">Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalleItems.map((item, idx) => (
                      <tr key={`${item.referencia_id || item.descripcion}-${idx}`} className="border-b border-white/5 hover:bg-white/[0.03]">
                        <td className="px-5 py-3 text-slate-300 text-sm whitespace-nowrap">{item.fecha || "—"}</td>
                        <td className="px-5 py-3 text-white text-sm font-medium">
                          {item.contraparte || "—"}
                          {item.documento && <p className="text-slate-500 text-xs mt-0.5">Doc: {item.documento}</p>}
                        </td>
                        <td className="px-5 py-3 text-slate-300 text-sm">
                          {item.descripcion || "—"}
                          {item.referencia_id && <p className="text-slate-600 text-[11px] mt-0.5">ID: {String(item.referencia_id).slice(0, 12)}</p>}
                        </td>
                        <td className="px-5 py-3 text-slate-400 text-sm">{item.cuenta_nombre || "—"}</td>
                        <td className={`px-5 py-3 text-right font-heading font-semibold text-sm ${detalleBalance.tipo === "Ingreso" ? "text-emerald-300" : "text-red-300"}`}>
                          {fmtPYG(item.monto_pyg || 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="p-5 border-t border-white/10 bg-white/[0.03] flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <p className="text-slate-500 text-xs">
                Mostrando {detalleItems.length} de {(detalleBalance.items || []).length} registro{(detalleBalance.items || []).length !== 1 ? "s" : ""}
              </p>
              <div className="flex items-center gap-3">
                <span className="text-slate-400 text-sm">Total visible</span>
                <span className={`font-heading font-bold text-lg ${detalleBalance.tipo === "Ingreso" ? "text-emerald-300" : "text-red-300"}`}>
                  {fmtPYG(detalleTotal)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ MODAL PAGO IVA ══ */}
      {showPagoIvaModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <div>
                <h2 className="text-white font-heading font-bold text-lg">Registrar pago IVA</h2>
                <p className="text-slate-400 text-sm mt-0.5">{mesLabel(periodo)}</p>
              </div>
              <button onClick={() => setShowPagoIvaModal(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handlePagoIva} className="p-6 space-y-4">
              {ivaData?.saldo_pendiente_acumulado > 0 && (
                <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 flex justify-between">
                  <span className="text-orange-300 text-sm">Saldo IVA acumulado</span>
                  <span className="text-orange-300 font-bold">{fmtPYG(ivaData.saldo_pendiente_acumulado)}</span>
                </div>
              )}
              <div>
                <label className="text-slate-400 text-xs block mb-1">Descripción</label>
                <input value={pagoIvaForm.descripcion} onChange={e => setPagoIvaForm(f => ({ ...f, descripcion: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
                />
              </div>
              <div>
                <label className="text-slate-400 text-xs block mb-1">Monto (PYG) *</label>
                <input type="number" min="0" step="1" value={pagoIvaForm.monto} onChange={e => setPagoIvaForm(f => ({ ...f, monto: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
                  required
                />
              </div>
	              <div className="grid grid-cols-2 gap-3">
	                <div>
	                  <label className="text-slate-400 text-xs block mb-1">Periodo IVA *</label>
	                  <input type="month" value={pagoIvaForm.periodo_iva} onChange={e => setPagoIvaForm(f => ({ ...f, periodo_iva: e.target.value, descripcion: f.descripcion || `Pago IVA ${mesLabel(e.target.value)}` }))}
	                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
	                    required
	                  />
	                </div>
	                <div>
	                  <label className="text-slate-400 text-xs block mb-1">Fecha pago *</label>
	                  <DateInput value={pagoIvaForm.fecha_pago} onChange={e => setPagoIvaForm(f => ({ ...f, fecha_pago: e.target.value }))}
	                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
	                    required
	                  />
	                </div>
	              </div>
	              <div>
	                <label className="text-slate-400 text-xs block mb-1">Cuenta *</label>
	                <select value={pagoIvaForm.cuenta_id} onChange={e => setPagoIvaForm(f => ({ ...f, cuenta_id: e.target.value }))}
	                  className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
	                  required>
	                  <option value="">Seleccionar</option>
	                  {cuentasBancarias.filter(c => c.moneda === "PYG").map(c => (
	                    <option key={c.id} value={c.id}>{c.nombre}</option>
	                  ))}
	                </select>
	              </div>
	              <div>
                <label className="text-slate-400 text-xs block mb-1">Notas</label>
                <input value={pagoIvaForm.notas} onChange={e => setPagoIvaForm(f => ({ ...f, notas: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
                  placeholder="Comprobante, banco, referencia..."
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowPagoIvaModal(false)} className="flex-1 px-4 py-2.5 bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg text-sm">
                  Cancelar
                </button>
                <button type="submit" disabled={savingPagoIva} className="flex-1 px-4 py-2.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
                  {savingPagoIva ? "Guardando..." : "Registrar pago"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
